import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AuthHeading } from '../../../../../components/auth/auth-heading';
import { OnboardingHeader } from '../../../../../components/auth/onboarding-header';
import { Button } from '../../../../../components/ui/button';
import { SearchInput } from '../../../../../components/ui/input';
import { OptionCard } from '../../../../../components/ui/option-card';
import { useOnboardingStore } from '../../../../../lib/onboarding-store';
import type { OnboardingDraft } from '../../../../../lib/onboarding';

export const Route = createFileRoute('/subdomain/app/_auth/onboarding/industry')({
  component: IndustryStep,
});

type Industry = NonNullable<OnboardingDraft['industry']>;

const INDUSTRIES: { key: Industry; label: string }[] = [
  { key: 'restaurant', label: 'Restaurant, café or cloud kitchen' },
  { key: 'healthcare', label: 'Clinic, hospital or healthcare' },
  { key: 'education', label: 'School, coaching or education' },
  { key: 'retail', label: 'Retail store or showroom' },
  { key: 'local_services', label: 'Mechanic or hardware' },
  // Not in the default list (the design shows five rows) but reachable through search.
  { key: 'real_estate', label: 'Real estate or property' },
  { key: 'other', label: 'Something else' },
];
const DEFAULT_ROWS = INDUSTRIES.slice(0, 5);

function IndustryStep() {
  const navigate = useNavigate();
  const { draft, updateDraft } = useOnboardingStore();
  const [query, setQuery] = useState('');

  const needle = query.trim().toLowerCase();
  const matches = needle ? INDUSTRIES.filter((i) => i.label.toLowerCase().includes(needle)) : DEFAULT_ROWS;
  // Nothing matched: offer what they typed as their own industry.
  const typedOther =
    needle && matches.length === 0 ? [{ key: 'other' as const, label: `Something else: “${query.trim()}”` }] : [];
  // Keep an already-chosen option visible after the search is cleared.
  const picked = INDUSTRIES.find((i) => i.key === draft.industry);
  const base = [...matches, ...typedOther];
  const rows = picked && !base.some((m) => m.key === picked.key) ? [...base, picked] : base;
  const otherNeedsText = draft.industry === 'other' && !draft.customIndustry.trim();

  const choose = (key: Industry) =>
    updateDraft({ industry: key, customIndustry: key === 'other' ? query.trim() || draft.customIndustry : '' });

  return (
    <>
      <OnboardingHeader step={1} backTo="/subdomain/app/welcome" />
      <AuthHeading
        title="What kind of business do you run?"
        subtitle="We’ll tailor your growth plan to your industry."
      />

      <SearchInput
        placeholder="Search business industry"
        aria-label="Search business industry"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onClear={() => setQuery('')}
      />

      <div role="group" aria-label="Industry" className="flex flex-col gap-(--mbm-space-3)">
        {rows.map((item) => (
          <OptionCard
            key={item.key}
            label={item.label}
            selected={draft.industry === item.key}
            onClick={() => choose(item.key)}
          />
        ))}
      </div>

      <Button
        fullWidth
        disabled={!draft.industry || otherNeedsText}
        onClick={() => navigate({ to: '/subdomain/app/onboarding/business' })}
      >
        Continue
      </Button>
    </>
  );
}
